import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FilterFacetValue } from '~app/modules/catalog/models';
import { Params } from '@angular/router';
import { Observable } from 'rxjs/internal/Observable';
import { select, Store } from '@ngrx/store';
import { isBlackDealTagVisible, isTagsVisible } from '~app/modules/filters/store';
import { map, takeUntil, tap } from 'rxjs/operators';
import * as fromFilters from '~app/modules/filters/store';
import { FiltersService } from '~app/modules/filters/services/filters.service';
import { FilterTagService } from '~shared/services/filter-tag.service';
import { Attribute, PriceRange, Promotion, Tag, VolumePrice } from '~app/modules/filters/models';
import { combineLatest, Subject } from 'rxjs';
import { AttributeOption, PriceFilterRange, RangeFilter } from '~shared/models';
import { FILTER_ATTRIBUTE_KEYS, FILTER_RANGE_KEYS } from '~app/modules/filters/constants';
import { getRouterState, RouterStateUrl } from '~core/store';
import { AttributeModalNewDesignComponent } from '~app/modules/filters/components';
import { MatDialog } from '@angular/material/dialog';
import { Category } from '~app/modules/generic/models';
import { BlackDeal } from '~app/modules/filters/models/black-deal.model';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';

@Component({
  selector: 'app-filter-facets-carousel',
  templateUrl: './filter-facets-carousel.component.html',
  styleUrls: ['./filter-facets-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterFacetsCarouselComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Output() togglePriceFilter: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleCategoryFilter: EventEmitter<void> = new EventEmitter<void>();
  @Output() togglePromotionFilter: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleAttributeFilter: EventEmitter<FilterFacetValue> = new EventEmitter<FilterFacetValue>();
  @Output() toggleRangeFilter: EventEmitter<string> = new EventEmitter<string>();

  @Input() queryParams: Params;
  @Input() isMobileOrTablet: boolean;
  @Input() isMobile: boolean;
  @Input() isTablet: boolean;
  @Input() isDesktop: boolean;
  @Input() fromBrandPage: boolean;

  @ViewChild('container') container: ElementRef;
  @ViewChild('carousel') carousel: ElementRef;
  @ViewChild('carouselInner') carouselInner: ElementRef;

  isTagsVisible: boolean;
  isTagsVisible$: Observable<boolean>;
  isBlackDealsTagVisible: boolean;
  isBlackDealsTagVisible$: Observable<boolean>;
  blackDeal$: Observable<BlackDeal>;
  blackDealTag$: Observable<boolean>;
  blackDealTag: boolean;
  promotion$: Observable<Promotion>;
  promotionTag$: Observable<boolean>;
  promotionTag: boolean;
  volumePriceTag: boolean;
  volumePriceTag$: Observable<boolean>;
  volumePrice$: Observable<VolumePrice>;
  rebuyGuarantee$: Observable<AttributeOption>;
  rebuyGuaranteeTag$: Observable<string>;
  rebuyGuaranteeTag: string;
  rebuyGuarantee: AttributeOption;
  priceRange$: Observable<PriceRange>;
  priceFilterTag$: Observable<PriceFilterRange>;
  attributes$: Observable<Attribute[]>;
  modifiedSelectedFilterData: FilterFacetValue[] = [];
  routerStateParams;
  widthTag: RangeFilter;
  heightTag: RangeFilter;
  lengthTag: RangeFilter;
  diameterTag: RangeFilter;
  widthTag$: Observable<RangeFilter>;
  heightTag$: Observable<RangeFilter>;
  lengthTag$: Observable<RangeFilter>;
  diameterTag$: Observable<RangeFilter>;
  attributeTags$: Observable<Tag<Attribute>[]>;
  categoriesTags$: Observable<Tag<Category>[]>;

  shouldShowRightArrow: boolean;
  shouldShowLeftArrow: boolean;

  private unsubscribe$: Subject<any> = new Subject<void>();
  private activeRoute$: Observable<RouterStateUrl> = this.store.pipe(select(getRouterState));
  isBlackDealsEnabled = this.blackDealsFeatureFlagService.isBlackDealsEnabled();

  constructor(
    private store: Store<fromFilters.State>,
    private cd: ChangeDetectorRef,
    private filtersService: FiltersService,
    private filterTagsService: FilterTagService,
    private dialog: MatDialog,
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService
  ) {}

  ngOnInit(): void {
    this.blackDeal$ = this.store.pipe(select(fromFilters.getBlackDealFilters));
    this.blackDealTag$ = this.filterTagsService.blackDealTag;

    this.promotion$ = this.store.pipe(select(fromFilters.getPromotion));
    this.promotionTag$ = this.filterTagsService.promotionTag;

    this.volumePriceTag$ = this.filterTagsService.volumePriceTag;
    this.volumePrice$ = this.store.pipe(select(fromFilters.getVolumePrice));
    this.rebuyGuarantee$ = this.store.pipe(select(fromFilters.getRebuyGuarantee));
    this.rebuyGuarantee$?.pipe(takeUntil(this.unsubscribe$)).subscribe((rebuyGuarantee: AttributeOption) => {
      this.rebuyGuarantee = rebuyGuarantee;
    });
    this.priceFilterTag$ = this.filterTagsService.priceFilterTags;
    this.priceRange$ = this.store.select(fromFilters.getPriceRange);
    this.widthTag$ = this.filterTagsService.widthTags;
    this.widthTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((widthTag: RangeFilter) => {
      this.widthTag = widthTag;
    });
    this.heightTag$ = this.filterTagsService.heightTags;
    this.heightTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((heightTag: RangeFilter) => {
      this.heightTag = heightTag;
    });
    this.lengthTag$ = this.filterTagsService.lengthTags;
    this.lengthTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((lengthTag: RangeFilter) => {
      this.lengthTag = lengthTag;
    });
    this.diameterTag$ = this.filterTagsService.diameterTags;
    this.diameterTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((diameterTag: RangeFilter) => {
      this.diameterTag = diameterTag;
    });
    this.categoriesTags$ = this.filterTagsService.categoriesTags;

    this.attributes$ = this.store.pipe(
      select(fromFilters.getAttributes),
      map((attributes: Attribute[]) => {
        return attributes?.filter((attribute: Attribute) =>
          this.fromBrandPage
            ? (attribute.id !== 'brand' && attribute?.options && attribute?.options?.length) || attribute?.optionsRange
            : (attribute?.options && attribute?.options?.length) || attribute?.optionsRange
        );
      })
    );
    this.blackDealTag$.pipe(takeUntil(this.unsubscribe$)).subscribe((blackDealTag: boolean) => {
      this.blackDealTag = blackDealTag;
    });
    this.promotionTag$ = this.filterTagsService.promotionTag;
    this.promotionTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((promotionTag: boolean) => {
      this.promotionTag = promotionTag;
    });
    this.volumePriceTag$ = this.filterTagsService.volumePriceTag;
    this.volumePriceTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((volumePriceTag: boolean) => {
      this.volumePriceTag = volumePriceTag;
    });
    this.rebuyGuaranteeTag$ = this.filterTagsService.rebuyGuaranteeTag;
    this.rebuyGuaranteeTag$?.pipe(takeUntil(this.unsubscribe$)).subscribe((rebuyGuaranteeTag: string) => {
      this.rebuyGuaranteeTag = rebuyGuaranteeTag;
    });
    this.priceFilterTag$ = this.filterTagsService.priceFilterTags;
    this.attributeTags$ = this.filterTagsService.attributesTags;

    combineLatest(
      this.attributes$,
      this.attributeTags$,
      this.widthTag$,
      this.heightTag$,
      this.lengthTag$,
      this.diameterTag$
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([attributes, filterValues, widthTag, heightTag, lengthTag, diameterTag]) => {
        this.modifiedSelectedFilterData = this.getModifiedSelectedFilterData(
          attributes,
          filterValues,
          widthTag,
          heightTag,
          lengthTag,
          diameterTag
        );
      });

    this.activeRoute$.pipe(takeUntil(this.unsubscribe$)).subscribe(({ queryParams }: RouterStateUrl) => {
      this.routerStateParams = queryParams;
    });

    this.initialTagVisible();
    this.initialBlackDealTagVisible();
    this.isTagsVisible$.pipe(takeUntil(this.unsubscribe$)).subscribe((tag) => {
      this.isTagsVisible = tag;
    });
    this.isBlackDealsTagVisible$.pipe(takeUntil(this.unsubscribe$)).subscribe((tag) => {
      this.isBlackDealsTagVisible = tag;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
    this.shouldShowRightArrow = this.getShouldShowRightArrow();
    this.carousel?.nativeElement?.scrollTo({
      left: 0,
      behaviour: 'smooth',
    });
    this.cd.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
    this.shouldShowRightArrow = this.getShouldShowRightArrow();
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  clearTags(): void {
    this.filtersService.resetCategoryFilter();
    this.filterTagsService.clearTags(this.queryParams);
  }
  onBlackDealFilterFacetClick() {
    const blackDealFilterChangedPayload = {
      blackFriday: this.blackDealTag ? {} : true,
    };
    this.filtersService.filterByBlackDeals(blackDealFilterChangedPayload);
  }

  removeBlackDealTag(blackDealTag: boolean) {
    this.filterTagsService.removeBlackDealTag(blackDealTag);
  }
  removePromotionTag(volumePrices: boolean): void {
    this.filterTagsService.removePromotionTag(volumePrices);
  }

  onPromotionFilterFacetClick(): void {
    const promotionFilterChangedPayload = {
      promotion: this.promotionTag ? {} : true,
      volumePrices: this.volumePriceTag,
    };
    this.filtersService.filterByPromotion(promotionFilterChangedPayload);
  }

  public prevClick(): void {
    this.carousel?.nativeElement.scrollTo({
      left: this.carousel?.nativeElement.scrollLeft - 100,
      behaviour: 'smooth',
    });
  }

  public nextClick(): void {
    this.carousel?.nativeElement.scrollTo({
      left: this.carousel?.nativeElement.scrollLeft + 100,
      behaviour: 'smooth',
    });
    this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
    this.shouldShowRightArrow = this.getShouldShowRightArrow();
    this.cd.detectChanges();
  }

  public onScroll(): void {
    this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
    this.shouldShowRightArrow = this.getShouldShowRightArrow();
    this.cd.detectChanges();
  }

  onVolumePriceFilterFacetClick(): void {
    this.filtersService.filterByVolumePrice({
      volumePrices: this.volumePriceTag ? {} : true,
      promotion: this.promotionTag,
    });
  }

  removeVolumePriceTag(promotion: boolean): void {
    this.filterTagsService.removeVolumePriceTag(promotion);
  }

  onRebuyGuaranteeFilterFacetClick(rebuyGuaranteeAttrOption: string): void {
    const currentlySelectedAttributes = this.queryParams?.filter?.options;
    let selectedCharacteristics: string[] = currentlySelectedAttributes?.characteristics
      ? Object.values(currentlySelectedAttributes?.characteristics)
      : [];

    if (this.rebuyGuaranteeTag) {
      selectedCharacteristics = selectedCharacteristics?.filter(
        (attrId: string) => attrId !== FILTER_ATTRIBUTE_KEYS.REBUY_GUARANTEE_OPTION
      );
    } else {
      selectedCharacteristics?.push(rebuyGuaranteeAttrOption);
    }

    const characteristics = selectedCharacteristics.reduce((obj, currentValue, index) => {
      obj[index] = currentValue;
      return obj;
    }, {});
    const attrs = {
      selectedAttrs: {
        ...currentlySelectedAttributes,
        characteristics,
      },
      content: !!rebuyGuaranteeAttrOption ? rebuyGuaranteeAttrOption : '',
      attributeId: FILTER_ATTRIBUTE_KEYS.CHARACTERISTICS,
    };
    this.filtersService.filterByAttribute(attrs, FILTER_ATTRIBUTE_KEYS.CHARACTERISTICS);
  }

  removeRebuyGuaranteeTag(rebuyGuaranteeAttrOption: AttributeOption): void {
    const currentlySelectedAttributes = this.queryParams?.filter?.options;
    let selectedCharacteristics: string[] = currentlySelectedAttributes?.characteristics
      ? Object.values(currentlySelectedAttributes?.characteristics)
      : [];

    selectedCharacteristics = selectedCharacteristics?.filter(
      (attrId: string) => attrId !== FILTER_ATTRIBUTE_KEYS.REBUY_GUARANTEE_OPTION
    );

    const characteristics = selectedCharacteristics.reduce((obj, currentValue, index) => {
      obj[index] = currentValue;
      return obj;
    }, {});
    const attrs = {
      selectedAttrs: {
        ...currentlySelectedAttributes,
        characteristics,
      },
      content: !!rebuyGuaranteeAttrOption ? rebuyGuaranteeAttrOption?.id : '',
      attributeId: FILTER_ATTRIBUTE_KEYS.CHARACTERISTICS,
    };
    this.filtersService.filterByAttribute(attrs, FILTER_ATTRIBUTE_KEYS.CHARACTERISTICS);
  }

  onPriceFilterFacetClick(): void {
    this.togglePriceFilter.emit();
  }

  removeAttributeTag(attribute: FilterFacetValue | Attribute, event): void {
    event.stopPropagation();

    if (
      attribute.id === FILTER_RANGE_KEYS.WIDTH ||
      attribute.id === FILTER_RANGE_KEYS.HEIGHT ||
      attribute.id === FILTER_RANGE_KEYS.LENGTH ||
      attribute.id === FILTER_RANGE_KEYS.DIAMETER
    ) {
      this.filtersService.resetRangeFilter(attribute.id);
    } else {
      this.filtersService.resetAttributeFilter(attribute.id);
    }
  }

  removeCategoryTag(): void {
    this.filtersService.resetCategoryFilter();
  }

  isFilterValueExists(filterFacetValue: FilterFacetValue | Attribute): boolean {
    return this.modifiedSelectedFilterData?.some((item: FilterFacetValue) => {
      return (
        item.id === filterFacetValue?.id ||
        ((item.id === FILTER_RANGE_KEYS.WIDTH ||
          item.id === FILTER_RANGE_KEYS.HEIGHT ||
          item.id === FILTER_RANGE_KEYS.LENGTH ||
          item.id === FILTER_RANGE_KEYS.DIAMETER) &&
          item.id === filterFacetValue.id)
      );
    });
  }

  removePriceTag(event: Event): void {
    event.stopPropagation();
    this.filterTagsService.removePriceTag();
  }

  onAttributeFilterFacetClick(attribute: Attribute): void {
    const isBrandOrSeller: boolean = attribute?.id === 'brand' || attribute?.id === 'seller';
    const isSearchableAttribute: boolean = isBrandOrSeller && this.isMobileOrTablet;

    if (isSearchableAttribute) {
      let width: string = '100%';
      width = this.isTablet ? '60%' : width;
      width = this.isDesktop ? '40%' : width;

      const options = {
        width,
        autoFocus: false,
        maxWidth: '690px',
        backdropClass: 'attr-options-modal',
        panelClass: 'attr-options-modal__container',
      } as any;
      this.dialog.open(AttributeModalNewDesignComponent, {
        ...options,
        data: {
          attribute,
          isMobileOrTablet: this.isMobileOrTablet,
          filterFields: this.getAttributeOptionsFields(attribute),
          isSpecificAttribute: true,
          queryParams: this.routerStateParams,
        },
      });

      return;
    }

    if (attribute?.optionsRange) {
      let optionsRange: RangeFilter;

      switch (attribute?.id) {
        case FILTER_RANGE_KEYS?.WIDTH:
          optionsRange = this.widthTag;
          break;
        case FILTER_RANGE_KEYS?.HEIGHT:
          optionsRange = this.heightTag;
          break;
        case FILTER_RANGE_KEYS?.LENGTH:
          optionsRange = this.lengthTag;
          break;
        case FILTER_RANGE_KEYS?.DIAMETER:
          optionsRange = this.diameterTag;
          break;
      }

      this.toggleAttributeFilter.emit({
        id: attribute?.id,
        name: attribute?.name,
        optionsRange: optionsRange,
      });
    } else {
      this.toggleAttributeFilter.emit({
        id: attribute?.id,
        name: attribute?.name,
        options: [attribute?.options[0]?.name],
      });
    }
  }

  getModifiedSelectedFilterData(
    attributes: Attribute[],
    filterValues: Tag<Attribute>[],
    widthTag: RangeFilter,
    heightTag: RangeFilter,
    lengthTag: RangeFilter,
    diameterTag: RangeFilter
  ): FilterFacetValue[] {
    let modifiedSelectedFilterData: FilterFacetValue[] = filterValues.reduce(
      (accumulated: FilterFacetValue[], attributeTag: Tag<Attribute>) => {
        const option: AttributeOption = attributeTag.value.options[0];
        if (option?.id !== 'int_rbg') {
          const key: string = attributeTag.value.id;
          const existingEntry: FilterFacetValue = accumulated.find(
            (filterFacetValue: FilterFacetValue) => filterFacetValue.id === key
          );

          if (!existingEntry) {
            accumulated.push({
              id: attributeTag.value.id,
              name: attributeTag.value.name,
              options: [option?.name],
            });
          } else {
            existingEntry.options.push(attributeTag.value.options[0].name);
          }
        }

        return accumulated;
      },
      []
    );
    if (widthTag) {
      const widthAttr: Attribute = attributes.find(
        (attribute: Attribute): boolean => attribute?.id === FILTER_RANGE_KEYS.WIDTH
      );
      modifiedSelectedFilterData.push({
        id: widthTag.type,
        name: widthAttr?.name,
        optionsRange: widthTag,
        options: [],
      });
    }
    if (heightTag) {
      const heightAttr: Attribute = attributes.find(
        (attribute: Attribute): boolean => attribute?.id === FILTER_RANGE_KEYS.HEIGHT
      );
      modifiedSelectedFilterData.push({
        id: heightTag.type,
        name: heightAttr?.name,
        optionsRange: heightTag,
        options: [],
      });
    }
    if (lengthTag) {
      const lengthAttr: Attribute = attributes.find((attribute: Attribute): boolean => {
        return attribute?.id === FILTER_RANGE_KEYS.LENGTH;
      });

      modifiedSelectedFilterData.push({
        id: lengthTag.type,
        name: lengthAttr?.name,
        optionsRange: lengthTag,
        options: [],
      });
    }
    if (diameterTag) {
      const diameterAttr: Attribute = attributes.find((attribute: Attribute): boolean => {
        return attribute?.id === FILTER_RANGE_KEYS.DIAMETER;
      });

      modifiedSelectedFilterData.push({
        id: diameterTag.type,
        name: diameterAttr?.name,
        optionsRange: diameterTag,
        options: [],
      });
    }

    return modifiedSelectedFilterData;
  }

  onCategoryFilterFacetClick(): void {
    this.toggleCategoryFilter.emit();
  }

  private getAttributeValueFromUrl(attr: string, attrValue: string): boolean {
    const { filter: { options = {} } = {} } = this.queryParams || {};

    if (options[`${attr}`]) {
      const values = Object.values(options[`${attr}`]) || [];
      return values.some((v) => v.toString() === attrValue);
    } else {
      return false;
    }
  }

  private getShouldShowLeftArrow(): boolean {
    const container = this.carousel?.nativeElement;
    const innerCarousel = this.carouselInner?.nativeElement;

    const contenedorRect = container?.getBoundingClientRect();
    const elementoRect = innerCarousel?.getBoundingClientRect();

    return Math.floor(elementoRect?.left) < Math.floor(contenedorRect?.left);
  }

  private getShouldShowRightArrow(): boolean {
    const container = this.carousel?.nativeElement;
    const innerCarousel = this.carouselInner?.nativeElement;

    const contenedorRect = container?.getBoundingClientRect();
    const elementoRect = innerCarousel?.getBoundingClientRect();

    return Math.floor(elementoRect?.right) > Math.floor(contenedorRect?.right);
  }

  private initialTagVisible(): void {
    this.isTagsVisible$ = this.store.pipe(
      select(isTagsVisible),
      tap(() => this.cd.markForCheck())
    );
  }

  private initialBlackDealTagVisible(): void {
    this.isBlackDealsTagVisible$ = this.store.pipe(
      select(isBlackDealTagVisible),
      tap(() => {
        this.cd.markForCheck();
      })
    );
  }

  private getAttributeOptionsFields(attribute: Attribute) {
    return attribute?.options.reduce(
      (acc, attrOption) => ({
        ...acc,
        [`${attribute.id}--${attrOption.value}`]: this.getAttributeValueFromUrl(attribute.id, attrOption.value),
      }),
      {}
    );
  }
}
